import { Cache, ImageLoader, Loader } from 'three';
import Deferred from '.././common/deferred';
import { getSpaceType } from '../common/utils';
import { CustomObject } from '../types/data';
import ApiService from './api-service';
import DataService from './data-service';

Cache.enabled = true;

class PreloadService {
  private maxParallelLoadings: number;
  private activeLoadings: number;
  private queue: {
    url: string;
    deferred: Deferred<any>;
    fileLoader: Loader;
    job: () => void;
  }[];

  constructor() {
    this.maxParallelLoadings = 6;
    this.activeLoadings = 0;
    this.queue = [];
  }

  loadImage = (url: string) => {
    const inQueue = this.queue.find(itm => itm.url === url);
    if (inQueue) {
      return inQueue.deferred.promise;
    }

    const inCache = Cache.get(url);
    if (inCache) {
      return Promise.resolve(inCache);
    }

    const deferred = new Deferred<any>();
    const self = this;

    const queueItem = {
      url,
      deferred,
      fileLoader: new ImageLoader(),
      async job() {
        self.activeLoadings += 1;
        try {
          const res = await this.fileLoader.loadAsync(this.url);

          this.deferred.resolve(res);
        } catch {
          this.deferred.reject(`File preload failed: ${this.url}`);
        } finally {
          self.activeLoadings -= 1;
          self.nextInQueue();
        }
      },
    };

    this.queue.push(queueItem);

    this.nextInQueue();

    return deferred.promise;
  };

  nextInQueue = () => {
    if (this.activeLoadings < this.maxParallelLoadings) {
      const next = this.queue.shift();
      if (next) {
        next.job();
      }
    }
  };

  //////////////

  loadNextPointsData = (university: string, pointIds: (string | number)[]) => {
    pointIds.forEach(id => {
      ApiService.getPoint(university, id)
        .then((payload: any) => {
          const isSuccess = DataService.isSuccess(payload.data);
          if (isSuccess) {
            const res: CustomObject = DataService.parseData(payload.data) as CustomObject;
            const pointType = getSpaceType(res);
            if (pointType === 'photo') {
              Object.values<string>(res.box).forEach(url =>
                this.loadImage(url).catch(e => console.log('log: failed load file: ', e))
              );
            } else if (pointType === 'video') {
              this.loadImage(res.video_thumbnail_url).catch(e => console.log('log: failed load file: ', e));
            }
          } else {
            Promise.reject("response data isn't valid");
          }
        })
        .catch(e => console.log(`log: Can't load point: id=${id}: `, e));
    });
  };
}

export default new PreloadService();
